import React, { Suspense, useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import BusinessIcon from '@material-ui/icons/Business';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CodeIcon from '@material-ui/icons/Code';
import CropLandscapeIcon from '@material-ui/icons/CropLandscape';
import CropPortraitIcon from '@material-ui/icons/CropPortrait';
import DeleteIcon from '@material-ui/icons/Delete';
import DescriptionIcon from '@material-ui/icons/Description';
import EventIcon from '@material-ui/icons/Event';
import ExposureIcon from '@material-ui/icons/Exposure';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import GetAppIcon from '@material-ui/icons/GetApp';
import HomeIcon from '@material-ui/icons/Home';
import PersonIcon from '@material-ui/icons/Person';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import ScheduleIcon from '@material-ui/icons/Schedule';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { useSnackbar } from 'notistack';

import FiltrosTabela from '../../Components/FiltrosTabela';
import { DialogoCreateEdit } from '../../Components/modulo-gerencial-dialogos';
import {
  TextInput,
  AutoComleteRemoto,
} from '../../Components/modulo-gerencial-formularios';
import {
  useForm,
  useFormEffect,
  useWatch,
} from '../../Components/modulo-gerencial-formularios/hooks';
import ControllerInput from '../../Components/modulo-gerencial-formularios/inputs/ControllerInput';
import { api, getListAPI } from '../../services';
import {
  PRIMARY_COLOR,
  STATUS_ATIVO,
  TURNO_12_HORAS,
  TURNO_MANHA,
  TURNO_NOITE,
} from '../../utils/constants';
import { extensaoDoTipo, VisualizadorArquivo } from '../Documentos/List';

const OPCOES_ACAO = [
  { valor: 'assinar', label: 'Assinar' },
  { valor: 'visualizar', label: 'Visualizar (marcar como lido)' },
  { valor: 'enviar_arquivo', label: 'Enviar arquivo' },
];

const OPCOES_ORIGEM = [
  { valor: 'upload', label: 'Arquivo enviado (PDF ou imagem)' },
  { valor: 'texto', label: 'Texto com variáveis' },
  { valor: 'por_colaborador', label: 'Um arquivo por colaborador' },
];

const OPCOES_RECORRENCIA = [
  { valor: 'unica', label: 'Única (uma vez por colaborador)' },
  { valor: 'sob_demanda', label: 'Sob demanda' },
  { valor: 'mensal', label: 'Mensal' },
  { valor: 'anual', label: 'Anual' },
];

const OPCOES_QUEM_ASSINA = [
  { valor: 'colaborador', label: 'Colaborador' },
  { valor: 'usuario_fixo', label: 'Pessoa fixa' },
];

const OPCOES_ABRANGENCIA = [
  { valor: 'um', label: 'Um colaborador' },
  { valor: 'varios', label: 'Vários colaboradores' },
  { valor: 'todos', label: 'Todos os colaboradores' },
];

// Tipos das variáveis personalizadas (o valor é pedido conforme o tipo ao gerar).
// Como a data aparece no texto: 12/10/2026 ou 12 de outubro de 2026.
export const FORMATOS_DATA = [
  { valor: 'normal', rotulo: 'Normal (12/10/2026)' },
  { valor: 'extenso', rotulo: 'Por extenso (12 de outubro de 2026)' },
];

export const TIPOS_VARIAVEL = [
  { valor: 'texto', rotulo: 'Texto', Icone: TextFieldsIcon },
  { valor: 'numero', rotulo: 'Número', Icone: ExposureIcon },
  { valor: 'data', rotulo: 'Data', Icone: EventIcon },
  { valor: 'horario', rotulo: 'Horário', Icone: ScheduleIcon },
];

// A lista de variáveis vem do servidor; aqui só se define o agrupamento do menu.
// Variável fora dos grupos (as do modelo ou nova no servidor) cai em "Personalizadas".
const GRUPOS_VARIAVEIS = [
  {
    titulo: 'Colaborador',
    Icone: PersonIcon,
    chaves: ['nome', 'cpf', 'funcao', 'email', 'telefone', 'data_admissao'],
  },
  { titulo: 'Endereço', Icone: HomeIcon, chaves: ['endereco', 'cep'] },
  { titulo: 'Empresa', Icone: BusinessIcon, chaves: ['empresa'] },
  {
    titulo: 'Documento',
    Icone: DescriptionIcon,
    chaves: ['data_hoje', 'competencia', 'modelo_nome'],
  },
];

const agruparVariaveis = (variaveis) => {
  const usadas = GRUPOS_VARIAVEIS.flatMap((grupo) => grupo.chaves);
  const grupos = GRUPOS_VARIAVEIS.map(({ titulo, Icone, chaves }) => ({
    titulo,
    Icone,
    itens: chaves
      .map((chave) => variaveis.find((variavel) => variavel.chave === chave))
      .filter(Boolean),
  }));
  // As criadas no modelo vão no topo do menu, perto do item "Criar variável".
  grupos.unshift({
    titulo: 'Personalizadas',
    Icone: CodeIcon,
    itens: variaveis.filter((variavel) => !usadas.includes(variavel.chave)),
  });
  return grupos.filter((grupo) => grupo.itens.length);
};

// O editor do texto só é baixado quando alguém abre um modelo de texto.
const EditorTexto = React.lazy(() => import('./EditorTexto'));

// Texto do modelo como na edição, sem poder editar. `valores` ({ chave: texto }) troca as
// variáveis personalizadas já preenchidas pelo texto digitado (prévia da geração).
export const TextoDoModelo = ({
  modelo,
  valores = {},
  maxHeight = 520,
  zoom = 1,
}) => {
  const [doSistema, setDoSistema] = useState([]);
  useEffect(() => {
    api
      .get('/modelos_documentos/variaveis')
      .then((response) => setDoSistema(response.data))
      .catch(() => setDoSistema([]));
  }, []);
  let personalizadas = [];
  try {
    personalizadas = JSON.parse(modelo.variaveis_personalizadas || '[]')
      .filter((item) => chaveDoRotulo(item.rotulo))
      .map(({ rotulo }) => ({ chave: chaveDoRotulo(rotulo), rotulo }));
  } catch (e) {
    personalizadas = [];
  }
  // Marcadores digitados não viram formatação na prévia.
  const texto = (
    modelo.conteudo_texto || ''
  ).replace(/\{\{\s*([^{}]*?)\s*\}\}/g, (achado, chave) =>
    (valores[chave] || '').trim()
      ? valores[chave].replace(/\*\*|~~|\+\+|\{\{|\}\}/g, '')
      : achado,
  );
  return (
    <Box mt={2} style={{ maxHeight, overflow: 'auto' }}>
      <div style={{ zoom }}>
        <Suspense
          fallback={
            <Typography variant="body2" color="textSecondary">
              Carregando texto...
            </Typography>
          }
        >
          <EditorTexto
            somenteLeitura
            value={texto}
            onChange={() => {}}
            grupos={[{ titulo: '', itens: [...doSistema, ...personalizadas] }]}
            titulo={modelo.nome}
            orientacao={modelo.orientacao}
            alturaMinima={200}
          />
        </Suspense>
      </div>
    </Box>
  );
};

export const FILTROS_COLABORADOR_VAZIOS = {
  is_user: '',
  is_gestor: '',
  funcao: null,
  loja: null,
  local: null,
  grupo: null,
  codinome: null,
  turno: '',
  interno: '',
  sexo: '',
};

const temFiltroColaboradores = (values) =>
  values.abrangencia === 'varios' &&
  Object.keys(filtrosDaLista(values.filtroColaboradores)).length > 0;

// Filtros salvos no modelo -> valores da faixa de filtros (os nomes vêm de `rotulos`).
const filtrosDoModelo = (salvo) => {
  if (!salvo) return FILTROS_COLABORADOR_VAZIOS;
  const { rotulos = {} } = salvo;
  const simNao = (valor) => {
    if (valor === undefined) return '';
    return valor ? 'sim' : 'nao';
  };
  const busca = (id, chave) => (id ? { id, nome: rotulos[chave] || id } : null);
  return {
    is_user: simNao(salvo.is_user),
    is_gestor: simNao(salvo.is_gestor),
    interno: simNao(salvo.is_funcionario_interno),
    funcao: busca(salvo.funcao_colaborador_id, 'funcao'),
    loja: salvo.empresa_id
      ? {
          id: salvo.empresa_id,
          nome_fantasia: rotulos.loja || salvo.empresa_id,
        }
      : null,
    local: busca(salvo.local_id, 'local'),
    grupo: busca(salvo.grupo_colaborador_id, 'grupo'),
    codinome: busca(salvo.codinome_colaborador_id, 'codinome'),
    turno: salvo.turno || '',
  };
};

const initialValues = {
  nome: '',
  descricao: '',
  acao: 'assinar',
  origem: 'upload',
  conteudo_texto: '',
  orientacao: 'retrato',
  variaveis: [],
  arquivo_pdf: null,
  tem_pdf: false,
  recorrencia: 'sob_demanda',
  quem_assina: 'colaborador',
  colaboradorFixo: null,
  maisAssinaturas: 'nao',
  assinantesAdicionais: [],
  abrangencia: '',
  colaboradores: [],
  filtroColaboradores: FILTROS_COLABORADOR_VAZIOS,
};

const isAssinar = (values) => values.acao === 'assinar';
const temConteudo = (values) => values.acao !== 'enviar_arquivo';
const isTexto = (values) => temConteudo(values) && values.origem === 'texto';
// Preenchido pelo RH, colaborador a colaborador, na conferência do detalhe do modelo (sem
// diálogo Gerar): "um arquivo por colaborador" ou texto com variáveis personalizadas, em
// recorrência única/mensal/anual (o "um arquivo por colaborador" sob demanda usa o Gerar).
const isPorColaborador = (values) =>
  ['unica', 'mensal', 'anual'].includes(values.recorrencia) &&
  (values.origem === 'por_colaborador' ||
    (['assinar', 'visualizar'].includes(values.acao) &&
      values.origem === 'texto' &&
      (values.variaveis || []).length > 0));
const isUpload = (values) => temConteudo(values) && values.origem === 'upload';
const LIMITE_TEXTO = 20000;
const TAMANHO_MAXIMO_PDF = 10 * 1024 * 1024;
// Modelos com abrangência: mensal/anual/única (geração automática, exceto o que é preenchido
// por colaborador) e envio de arquivo mensal/anual; a abrangência define os destinatários.
const isRecorrente = (values) =>
  ['mensal', 'anual'].includes(values.recorrencia) ||
  values.recorrencia === 'unica' ||
  isPorColaborador(values);
// Variáveis do modelo (valor digitado ao gerar): só em texto.
const usaVariaveis = (values) => isTexto(values);
export const chaveDoRotulo = (rotulo) =>
  rotulo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
const usaMaisAssinaturas = (values) =>
  isAssinar(values) && values.maisAssinaturas === 'sim';
// Com mais de uma assinatura a primeira é sempre o colaborador.
const isUsuarioFixo = (values) =>
  isAssinar(values) &&
  !usaMaisAssinaturas(values) &&
  values.quem_assina === 'usuario_fixo';
const usaLista = (values) =>
  isRecorrente(values) && ['um', 'varios'].includes(values.abrangencia);

// Campos escondidos continuam com validador, por isso cada um só valida
// quando se aplica à ação/recorrência escolhida.
const validadores = {
  nome: (value) => {
    if (!value || value.trim().length < 1) return 'Este campo é obrigatório';
    if (value.length > 120) return 'Não pode ser maior que 120 caracteres';
    return '';
  },
  descricao: () => '',
  acao: () => '',
  origem: () => '',
  conteudo_texto: (value, values) => {
    if (!isTexto(values)) return '';
    if (!value || !value.trim()) return 'Este campo é obrigatório';
    if (value.length > LIMITE_TEXTO)
      return `Não pode ser maior que ${LIMITE_TEXTO} caracteres`;
    return '';
  },
  variaveis: (value, values) => {
    if (!usaVariaveis(values)) return '';
    const rotulos = (value || []).map((item) => item.rotulo.trim());
    if (rotulos.some((rotulo) => !chaveDoRotulo(rotulo)))
      return 'Informe o nome de cada variável';
    if (rotulos.some((rotulo) => rotulo.length > 60))
      return 'O nome da variável não pode ter mais de 60 caracteres';
    const chaves = rotulos.map(chaveDoRotulo);
    if (new Set(chaves).size !== chaves.length)
      return 'Há variáveis com o mesmo nome';
    return '';
  },
  // Só o PDF do modelo novo é obrigatório; na edição vale o que já foi enviado.
  arquivo_pdf: (value, values) =>
    isUpload(values) && !value && !values.tem_pdf
      ? 'Selecione o arquivo do modelo (PDF ou imagem)'
      : '',
  tem_pdf: () => '',
  orientacao: () => '',
  recorrencia: () => '',
  quem_assina: (value, values) =>
    isAssinar(values) && !value ? 'Este campo é obrigatório' : '',
  colaboradorFixo: (value, values) =>
    isUsuarioFixo(values) && !value ? 'Informe o colaborador que assina' : '',
  maisAssinaturas: () => '',
  assinantesAdicionais: (value, values) => {
    if (!usaMaisAssinaturas(values)) return '';
    const total = value ? value.length : 0;
    if (total < 1) return 'Selecione ao menos 1 pessoa para a assinatura 2';
    if (total > 5) return 'No máximo 5 assinaturas adicionais';
    return '';
  },
  abrangencia: (value, values) =>
    isRecorrente(values) && !value ? 'Este campo é obrigatório' : '',
  colaboradores: (value, values) => {
    if (!usaLista(values)) return '';
    const total = value ? value.length : 0;
    if (values.abrangencia === 'um' && total !== 1)
      return 'Selecione exatamente 1 colaborador';
    if (total < 1 && !temFiltroColaboradores(values))
      return 'Selecione ao menos 1 colaborador ou use um filtro';
    return '';
  },
};

// JSON inválido no banco não quebra a edição do modelo: vira lista vazia.
const variaveisDoModelo = (json) => {
  try {
    return JSON.parse(json || '[]').map(({ rotulo, tipo, formato }) => ({
      rotulo,
      tipo: tipo || 'texto',
      ...(tipo === 'data' ? { formato: formato || 'normal' } : {}),
    }));
  } catch (e) {
    return [];
  }
};

const transformGetDados = (dados) => ({
  nome: dados.nome,
  descricao: dados.descricao || '',
  acao: dados.acao,
  origem: dados.origem || 'upload',
  conteudo_texto: dados.conteudo_texto || '',
  orientacao: dados.orientacao === 'paisagem' ? 'paisagem' : 'retrato',
  variaveis: variaveisDoModelo(dados.variaveis_personalizadas),
  arquivo_pdf: null,
  tem_pdf: Boolean(dados.arquivo_referencia),
  recorrencia: dados.recorrencia,
  quem_assina: dados.quem_assina || 'colaborador',
  maisAssinaturas: dados.mais_assinaturas ? 'sim' : 'nao',
  // As adicionais são guardadas como usuário; na tela aparece o colaborador de cada um.
  assinantesAdicionais: (dados.assinantesAdicionais || [])
    .filter((usuario) => usuario.colaborador)
    .map((usuario) => ({
      id: usuario.colaborador.id,
      nome: usuario.colaborador.nome,
    })),
  // O assinante fixo é guardado como usuário; na tela aparece o colaborador dele.
  colaboradorFixo:
    dados.usuarioFixo && dados.usuarioFixo.colaborador
      ? {
          id: dados.usuarioFixo.colaborador.id,
          nome: dados.usuarioFixo.colaborador.nome,
        }
      : null,
  abrangencia: dados.abrangencia || '',
  colaboradores: dados.colaboradores || [],
  filtroColaboradores: filtrosDoModelo(dados.filtro_colaboradores),
});

const transformSalvar = (dados) => ({
  nome: dados.nome,
  descricao: dados.descricao || null,
  acao: dados.acao,
  origem: temConteudo(dados) ? dados.origem : null,
  conteudo_texto: isTexto(dados) ? dados.conteudo_texto : null,
  orientacao: isTexto(dados) ? dados.orientacao : 'retrato',
  variaveis_personalizadas: usaVariaveis(dados) ? dados.variaveis : [],
  recorrencia: dados.recorrencia,
  quem_assina: isAssinar(dados)
    ? (usaMaisAssinaturas(dados) && 'colaborador') || dados.quem_assina
    : null,
  mais_assinaturas: usaMaisAssinaturas(dados),
  assinantes_adicionais: usaMaisAssinaturas(dados)
    ? dados.assinantesAdicionais.map((colaborador) => colaborador.id)
    : [],
  colaborador_fixo_id: isUsuarioFixo(dados) ? dados.colaboradorFixo.id : null,
  abrangencia: isRecorrente(dados) ? dados.abrangencia : null,
  colaboradores: usaLista(dados)
    ? dados.colaboradores.map((colaborador) => colaborador.id)
    : [],
  filtro_colaboradores:
    isRecorrente(dados) && dados.abrangencia === 'varios'
      ? {
          ...filtrosDaLista(dados.filtroColaboradores),
          rotulos: {
            funcao: (dados.filtroColaboradores.funcao || {}).nome,
            loja: (dados.filtroColaboradores.loja || {}).nome_fantasia,
            local: (dados.filtroColaboradores.local || {}).nome,
            grupo: (dados.filtroColaboradores.grupo || {}).nome,
            codinome: (dados.filtroColaboradores.codinome || {}).nome,
          },
        }
      : null,
});

// Explica o que acontece na recorrência escolhida, conforme a ação e o tipo de documento.
const textoDaRecorrencia = (getValue) => {
  const valores = {
    acao: getValue('acao'),
    origem: getValue('origem'),
    recorrencia: getValue('recorrencia'),
    variaveis: getValue('variaveis'),
  };
  const { acao, origem, recorrencia } = valores;
  const porColaborador = isPorColaborador(valores);
  const preenche =
    origem === 'texto' ? 'preenche as variáveis do texto' : 'envia o arquivo';
  if (recorrencia === 'sob_demanda') {
    if (acao === 'enviar_arquivo')
      return 'Sob demanda: o colaborador envia quando precisar (em Minhas pendências) ou o RH gera a pendência para ele. Pode repetir quantas vezes for preciso.';
    if (origem === 'por_colaborador')
      return 'Sob demanda: o RH gera quando necessário. Escolhe um colaborador por vez e envia o arquivo dele (obrigatório).';
    if (origem === 'texto' && (valores.variaveis || []).length)
      return 'Sob demanda: o RH gera quando necessário, um colaborador por vez, preenchendo as variáveis. Pode repetir quantas vezes for preciso.';
    return 'Sob demanda: o RH gera quando necessário, para um, vários ou todos os colaboradores. Pode repetir quantas vezes for preciso.';
  }
  const periodo = { unica: '', mensal: 'mês', anual: 'ano' }[recorrencia];
  if (recorrencia === 'unica') {
    if (acao === 'enviar_arquivo')
      return 'Única: o sistema cria uma pendência de envio para cada colaborador da abrangência que ainda não enviou, inclusive para quem entrar depois.';
    if (porColaborador)
      return `Única: o RH ${preenche} de cada colaborador da abrangência, um de cada vez, na aba Documentos do detalhe do modelo.`;
    return 'Única: o sistema gera sozinho um documento para cada colaborador da abrangência, uma só vez, inclusive para quem entrar depois.';
  }
  if (acao === 'enviar_arquivo')
    return `${
      recorrencia === 'mensal' ? 'Mensal' : 'Anual'
    }: a cada ${periodo} o sistema cria a pendência de envio para os colaboradores da abrangência.`;
  if (porColaborador)
    return `${
      recorrencia === 'mensal' ? 'Mensal' : 'Anual'
    }: a cada ${periodo} o RH ${preenche} de cada colaborador na aba Documentos do detalhe do modelo (ex.: contracheque). O passador de ${periodo} mostra quem ainda não tem.`;
  return `${
    recorrencia === 'mensal' ? 'Mensal' : 'Anual'
  }: o sistema gera sozinho um documento por colaborador a cada ${periodo}, a partir do ${periodo} seguinte ao da criação do modelo.`;
};

function SelectCampo({
  name,
  label,
  opcoes,
  ajuda,
  visible = true,
  deps = [],
}) {
  const { getValue } = useForm();
  return (
    <ControllerInput
      name={name}
      visible={visible}
      deps={deps}
      render={({ field }) => (
        <TextField
          {...field}
          helperText={
            field.helperText ||
            (typeof ajuda === 'function' ? ajuda(getValue) : ajuda)
          }
          select
          fullWidth
          variant="outlined"
          margin="normal"
          label={label}
        >
          {opcoes.map(({ valor, label: texto }) => (
            <MenuItem key={valor} value={valor}>
              {texto}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}

// Seção com barra colorida na lateral e título em destaque; some junto com
// os campos quando `visible` (função dos valores do formulário) é falsa.
function Secao({ titulo, visible = () => true, deps = [], children }) {
  const { formState, subscribeField } = useForm();
  const [, atualizar] = useState({});

  // Redesenha quando muda um dos campos de que `visible` depende (o store só
  // avisa quem se inscreve no campo).
  useEffect(() => {
    const cancelar = deps.map((campo) =>
      subscribeField(campo, () => atualizar({})),
    );
    return () => cancelar.forEach((cancela) => cancela());
  }, [deps.join(',')]);

  if (!visible(formState.values)) return null;
  return (
    <Box
      mt={3}
      pl={2}
      pb={1}
      borderLeft={4}
      borderColor="primary.main"
      style={{ borderLeftStyle: 'solid' }}
    >
      <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
        {titulo}
      </Typography>
      {children}
    </Box>
  );
}

// Campos lado a lado; um campo oculto deixa o outro ocupar a linha toda e,
// em tela estreita, empilham.
function Linha({ children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        columnGap: 16,
      }}
    >
      {children}
    </div>
  );
}

// Com responseType 'blob' o corpo do erro (JSON) também chega como Blob.
export async function mensagemDoErro(e, padrao = 'Erro ao gerar a prévia.') {
  try {
    const corpo = JSON.parse(await e.response.data.text());
    return corpo.message || padrao;
  } catch (erro) {
    return padrao;
  }
}

// Visualizador de PDF em diálogo; quem usa cuida de criar e liberar a URL.
// Escondido o visualizador do navegador, que mostraria o nome do blob (um código
// aleatório); o título e o download ficam no cabeçalho do diálogo.
export function VisualizadorPdf({
  url,
  tipo = 'application/pdf',
  titulo,
  subtitulo,
  onClose,
}) {
  function baixar() {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(titulo || 'modelo').replace(
      /[\\/:*?"<>|]/g,
      '-',
    )}.${extensaoDoTipo(tipo)}`;
    link.click();
  }

  return (
    <Dialog open={Boolean(url)} onClose={onClose} fullScreen>
      <Box
        display="flex"
        alignItems="center"
        px={2}
        py={1}
        style={{ gap: 12, backgroundColor: PRIMARY_COLOR, color: 'white' }}
      >
        <div style={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap style={{ fontWeight: 600 }}>
            {titulo || 'Modelo de documento'}
          </Typography>
          {subtitulo && (
            <Typography variant="caption" display="block" noWrap>
              {subtitulo}
            </Typography>
          )}
        </div>
        <Box flex={1} />
        <Tooltip title="Baixar arquivo">
          <IconButton size="small" onClick={baixar} style={{ color: 'white' }}>
            <GetAppIcon />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} style={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent style={{ padding: 0, display: 'flex' }}>
        {url && (
          <VisualizadorArquivo
            url={url}
            tipo={tipo}
            titulo="Visualização do arquivo"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Prévia do texto que está na tela com dados de exemplo. Não salva nada: o
// servidor só devolve o PDF.
function PreviewTexto() {
  const { getValue } = useForm();
  const { enqueueSnackbar } = useSnackbar();
  const [carregando, setCarregando] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  async function handlePreview() {
    setCarregando(true);
    try {
      const response = await api.post(
        '/modelos_documentos/preview',
        {
          nome: getValue('nome'),
          recorrencia: getValue('recorrencia'),
          conteudo_texto: getValue('conteudo_texto'),
          variaveis_personalizadas: getValue('variaveis') || [],
          orientacao: getValue('orientacao'),
        },
        { responseType: 'blob' },
      );
      setPdfUrl(URL.createObjectURL(response.data));
    } catch (e) {
      enqueueSnackbar(await mensagemDoErro(e), { variant: 'error' });
    } finally {
      setCarregando(false);
    }
  }

  function handleFechar() {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  }

  return (
    <>
      <Tooltip title="Prévia do PDF com dados de exemplo (nada é salvo)">
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={handlePreview}
            disabled={carregando}
          >
            {carregando ? <CircularProgress size={20} /> : <VisibilityIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <VisualizadorPdf
        url={pdfUrl}
        titulo={getValue('nome') || 'Prévia do documento'}
        subtitulo="Prévia com dados de exemplo · as variáveis personalizadas aparecem entre colchetes"
        onClose={handleFechar}
      />
    </>
  );
}

const SIM_NAO = [
  { id: 'sim', nome: 'Sim' },
  { id: 'nao', nome: 'Não' },
];

// Filtros da tela de Colaboradores, para montar a lista do modelo.
const buscarEm = (recurso, extra = {}) => async (texto) => {
  const { data } = await getListAPI(
    recurso,
    ['nome', 'ASC'],
    [1, 30],
    { nome: texto, ...extra },
    [],
  );
  return data;
};

export const filtrosDaLista = (f) => ({
  ...(f.is_user && { is_user: f.is_user === 'sim' }),
  ...(f.is_gestor && { is_gestor: f.is_gestor === 'sim' }),
  ...(f.interno && { is_funcionario_interno: f.interno === 'sim' }),
  ...(f.funcao && { funcao_colaborador_id: f.funcao.id }),
  ...(f.loja && { empresa_id: f.loja.id }),
  ...(f.local && { local_id: f.local.id }),
  ...(f.grupo && { grupo_colaborador_id: f.grupo.id }),
  ...(f.codinome && { codinome_colaborador_id: f.codinome.id }),
  ...(f.turno && { turno: f.turno }),
  ...(f.sexo && { sexo: f.sexo }),
});

export const CAMPOS_FILTRO_COLABORADOR = [
  { chave: 'is_user', rotulo: 'É usuário', tipo: 'selecao', opcoes: SIM_NAO },
  { chave: 'is_gestor', rotulo: 'É gestor', tipo: 'selecao', opcoes: SIM_NAO },
  {
    chave: 'funcao',
    rotulo: 'Função',
    tipo: 'busca',
    buscar: buscarEm('funcoes_colaboradores', { status: STATUS_ATIVO }),
  },
  {
    chave: 'loja',
    rotulo: 'Loja',
    tipo: 'busca',
    rotuloOpcao: 'nome_fantasia',
    buscar: async (texto) => {
      const { data } = await getListAPI(
        'empresa-loja',
        ['nome_fantasia', 'ASC'],
        [1, 100],
        {},
        [],
      );
      return data.filter((loja) =>
        (loja.nome_fantasia || '').toLowerCase().includes(texto.toLowerCase()),
      );
    },
  },
  {
    chave: 'local',
    rotulo: 'Local',
    tipo: 'busca',
    buscar: buscarEm('locais_setores', { status: STATUS_ATIVO }),
  },
  {
    chave: 'grupo',
    rotulo: 'Grupo',
    tipo: 'busca',
    buscar: buscarEm('grupos_colaborador'),
  },
  {
    chave: 'codinome',
    rotulo: 'Codinome',
    tipo: 'busca',
    buscar: buscarEm('codinomes_colaborador'),
  },
  {
    chave: 'turno',
    rotulo: 'Turno',
    tipo: 'selecao',
    opcoes: [
      { id: TURNO_MANHA, nome: 'Manhã' },
      { id: TURNO_NOITE, nome: 'Noite' },
      { id: TURNO_12_HORAS, nome: '12H' },
    ],
  },
  {
    chave: 'interno',
    rotulo: 'Funcionário interno',
    tipo: 'selecao',
    opcoes: SIM_NAO,
  },
];

function Form({ id }) {
  const filtrosColab = useWatch('filtroColaboradores');
  const abrangencia = useWatch('abrangencia');
  const acao = useWatch('acao');
  const origem = useWatch('origem');
  const recorrencia = useWatch('recorrencia');
  const variaveisForm = useWatch('variaveis');
  const { getValue, setValue } = useForm();
  const [variaveis, setVariaveis] = useState([]);
  const [dialogoVar, setDialogoVar] = useState(false);
  const [editorCheio, setEditorCheio] = useState(false);
  const [novaVar, setNovaVar] = useState('');
  const [tipoNovaVar, setTipoNovaVar] = useState('texto');
  const [formatoNovaVar, setFormatoNovaVar] = useState('normal');
  const [erroVar, setErroVar] = useState('');
  // O formulário não redesenha o diálogo ao mudar o valor: força depois de cada alteração.
  const [, atualizarVar] = useState({});
  const [pdfVisto, setPdfVisto] = useState(null);
  const [pdfSubtitulo, setPdfSubtitulo] = useState('');
  const [pdfTipo, setPdfTipo] = useState('application/pdf');
  const { enqueueSnackbar } = useSnackbar();

  function selecionarPdf(event, field) {
    const [selecionado] = event.target.files;
    // Permite escolher de novo o mesmo arquivo depois de uma recusa.
    event.target.value = '';
    if (!selecionado) return;
    if (
      !['application/pdf', 'image/jpeg', 'image/png'].includes(selecionado.type)
    ) {
      enqueueSnackbar('Selecione um PDF ou uma imagem JPG/PNG.', {
        variant: 'error',
      });
      return;
    }
    if (selecionado.size > TAMANHO_MAXIMO_PDF) {
      enqueueSnackbar('O arquivo deve ter no máximo 10 MB.', {
        variant: 'error',
      });
      return;
    }
    field.onChange({ target: { value: selecionado } });
  }

  function visualizarNovo(arquivo) {
    setPdfSubtitulo('Arquivo selecionado, ainda não enviado');
    setPdfTipo(arquivo.type);
    setPdfVisto(URL.createObjectURL(arquivo));
  }

  async function visualizarAtual() {
    try {
      const response = await api.get(`/modelos_documentos/${id}/pdf`, {
        responseType: 'blob',
      });
      setPdfSubtitulo('Arquivo do modelo');
      setPdfTipo(response.data.type || 'application/pdf');
      setPdfVisto(URL.createObjectURL(response.data));
    } catch (e) {
      enqueueSnackbar(await mensagemDoErro(e, 'Erro ao abrir o PDF.'), {
        variant: 'error',
      });
    }
  }

  function fecharPdf() {
    if (pdfVisto) URL.revokeObjectURL(pdfVisto);
    setPdfVisto(null);
  }

  // Lista de variáveis vem do servidor (fonte única, a mesma da validação).
  useEffect(() => {
    api
      .get('/modelos_documentos/variaveis')
      .then((response) => setVariaveis(response.data))
      .catch(() => setVariaveis([]));
  }, []);

  // Variáveis do modelo: criadas e listadas num diálogo (não ocupam espaço no formulário).
  const listaVar = getValue('variaveis') || [];

  function adicionarVariavel() {
    const rotulo = novaVar.trim();
    const chave = chaveDoRotulo(rotulo);
    if (!chave) setErroVar('Informe o nome da variável.');
    else if (
      variaveis.some((fixa) => fixa.chave === chave) ||
      listaVar.some((item) => chaveDoRotulo(item.rotulo) === chave)
    )
      setErroVar('Já existe uma variável com este nome.');
    else if (listaVar.length >= 10)
      setErroVar('O limite é de 10 variáveis por modelo.');
    else {
      setValue('variaveis', [
        ...listaVar,
        {
          rotulo,
          tipo: tipoNovaVar,
          ...(tipoNovaVar === 'data' ? { formato: formatoNovaVar } : {}),
        },
      ]);
      atualizarVar({});
      setNovaVar('');
      setTipoNovaVar('texto');
      setFormatoNovaVar('normal');
      setErroVar('');
    }
  }

  // O store só valida o campo que mudou; ao mudar um campo "pai", revalida
  // os dependentes para o erro refletir a regra atual.
  const revalidar = (nomes) =>
    nomes.forEach((nome) => setValue(nome, getValue(nome)));

  useFormEffect(['acao'], () => revalidar(['quem_assina', 'colaboradorFixo']));
  useFormEffect(['acao', 'origem', 'recorrencia'], () =>
    revalidar(['conteudo_texto', 'arquivo_pdf', 'variaveis']),
  );
  useFormEffect(['quem_assina', 'maisAssinaturas'], () =>
    revalidar(['colaboradorFixo']),
  );
  useFormEffect(['acao', 'maisAssinaturas'], () =>
    revalidar(['assinantesAdicionais']),
  );
  // "Um" aceita só 1 colaborador (fica o último escolhido); "Todos" não usa lista.
  useFormEffect(['abrangencia', 'colaboradores'], () => {
    const lista = getValue('colaboradores') || [];
    if (getValue('abrangencia') === 'todos' && lista.length)
      setValue('colaboradores', []);
    else if (getValue('abrangencia') === 'um' && lista.length > 1)
      setValue('colaboradores', lista.slice(-1));
  });
  useFormEffect(['recorrencia'], () =>
    revalidar(['abrangencia', 'colaboradores', 'variaveis']),
  );
  useFormEffect(['abrangencia'], () => revalidar(['colaboradores']));

  return (
    <div>
      <Secao titulo="Identificação">
        <TextInput name="nome" fullWidth label="Nome" autoFocus />
        <TextInput
          name="descricao"
          fullWidth
          multiline
          rows={2}
          label="Descrição / instrução ao colaborador"
        />
      </Secao>
      <Secao titulo="Conteúdo">
        <Linha>
          <SelectCampo
            name="acao"
            label="Ação"
            opcoes={OPCOES_ACAO}
            ajuda="Assinar, só visualizar ou pedir um arquivo ao colaborador"
          />
          <SelectCampo
            name="origem"
            label="Tipo de documento"
            opcoes={OPCOES_ORIGEM}
            deps={['acao']}
            visible={temConteudo}
          />
        </Linha>
        <ControllerInput
          name="arquivo_pdf"
          deps={['acao', 'origem', 'recorrencia', 'tem_pdf']}
          visible={isUpload}
          render={({ field }) => {
            const novo = field.value;
            const temAtual = getValue('tem_pdf');
            return (
              <Paper
                variant="outlined"
                style={{
                  marginTop: 16,
                  padding: 16,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 16,
                  borderStyle: 'dashed',
                  borderColor: field.error ? '#f44336' : undefined,
                }}
              >
                <PictureAsPdfIcon
                  fontSize="large"
                  color={novo || temAtual ? 'primary' : 'disabled'}
                />
                <Box flex="1 1 200px" minWidth={0}>
                  <Typography variant="subtitle2" noWrap>
                    {novo
                      ? novo.name
                      : temAtual
                      ? 'Arquivo do modelo já enviado'
                      : 'Nenhum arquivo selecionado'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={field.error ? 'error' : 'textSecondary'}
                  >
                    {field.error
                      ? field.helperText
                      : novo
                      ? `${(novo.size / 1024 / 1024).toFixed(
                          2,
                        )} MB · será enviado ao salvar`
                      : temAtual
                      ? 'Escolha outro apenas para substituir'
                      : 'PDF ou imagem (JPG/PNG), até 10 MB'}
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  {(novo || temAtual) && (
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() =>
                        novo ? visualizarNovo(novo) : visualizarAtual()
                      }
                    >
                      Visualizar
                    </Button>
                  )}
                  {novo && (
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() =>
                        field.onChange({ target: { value: null } })
                      }
                    >
                      Remover
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="primary"
                    component="label"
                    variant={novo || temAtual ? 'outlined' : 'contained'}
                    startIcon={<CloudUploadIcon />}
                  >
                    {novo
                      ? 'Trocar'
                      : temAtual
                      ? 'Substituir'
                      : 'Selecionar arquivo'}
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                      onChange={(e) => selecionarPdf(e, field)}
                    />
                  </Button>
                </Box>
              </Paper>
            );
          }}
        />
        <ControllerInput
          name="orientacao"
          deps={['acao', 'origem']}
          visible={isTexto}
          render={() => null}
        />
        <ControllerInput
          name="conteudo_texto"
          deps={['acao', 'origem', 'recorrencia', 'variaveis', 'orientacao']}
          visible={isTexto}
          render={({ field }) => {
            // Ações do texto (no lado direito da barra do editor): a mesma no formulário e na tela cheia.
            const acoes = (cheio) => (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <PreviewTexto />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={getValue('orientacao') || 'retrato'}
                  onChange={(event, valor) =>
                    valor && setValue('orientacao', valor)
                  }
                >
                  <ToggleButton value="retrato">
                    <Tooltip title="Página em retrato">
                      <CropPortraitIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="paisagem">
                    <Tooltip title="Página em paisagem">
                      <CropLandscapeIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip
                  title={
                    cheio
                      ? 'Concluir (sair da tela cheia)'
                      : 'Editar em tela cheia'
                  }
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setEditorCheio(!cheio)}
                  >
                    {cheio ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            );
            const grupos = agruparVariaveis([
              ...variaveis,
              ...(getValue('variaveis') || [])
                .filter((item) => chaveDoRotulo(item.rotulo))
                .map((item) => ({
                  chave: chaveDoRotulo(item.rotulo),
                  rotulo: item.rotulo,
                })),
            ]);
            const editor = (cheio) => (
              <Suspense
                fallback={
                  <Typography variant="body2" color="textSecondary">
                    Carregando editor...
                  </Typography>
                }
              >
                <EditorTexto
                  value={field.value || ''}
                  onChange={(texto) =>
                    field.onChange({ target: { value: texto } })
                  }
                  grupos={grupos}
                  titulo={getValue('nome')}
                  orientacao={getValue('orientacao')}
                  erro={Boolean(field.helperText)}
                  acoes={acoes(cheio)}
                  gerenciarVariaveis={
                    usaVariaveis({
                      acao: getValue('acao'),
                      origem: getValue('origem'),
                      recorrencia: getValue('recorrencia'),
                    })
                      ? {
                          rotulo: `Criar variável (${
                            (getValue('variaveis') || []).length
                          })`,
                          onClick: () => setDialogoVar(true),
                        }
                      : null
                  }
                  alturaMinima={cheio ? 900 : 420}
                />
              </Suspense>
            );
            const contador = (
              <Typography
                variant="caption"
                color={field.helperText ? 'error' : 'textSecondary'}
              >
                {field.helperText ||
                  `${(field.value || '').length}/${LIMITE_TEXTO}`}
              </Typography>
            );
            return (
              <>
                <Paper variant="outlined" style={{ marginTop: 16 }}>
                  <Box p={1}>{editorCheio ? null : editor(false)}</Box>
                  <Box px={2} pb={1} textAlign="right">
                    {contador}
                  </Box>
                </Paper>
                <Dialog
                  fullScreen
                  open={editorCheio}
                  onClose={() => setEditorCheio(false)}
                >
                  <Box flex={1} overflow="auto" px={2} pb={2}>
                    {editorCheio ? editor(true) : null}
                  </Box>
                  <Box px={4} py={1} textAlign="right">
                    {contador}
                  </Box>
                </Dialog>
              </>
            );
          }}
        />
      </Secao>
      <Dialog
        open={dialogoVar}
        onClose={() => setDialogoVar(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          style={{ gap: 8, backgroundColor: PRIMARY_COLOR, color: 'white' }}
        >
          <PlaylistAddIcon />
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Variáveis do documento
          </Typography>
          <Box flex={1} />
          <IconButton
            size="small"
            style={{ color: 'white' }}
            onClick={() => setDialogoVar(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Crie campos para preencher na hora de gerar o documento. Depois,
            insira no texto por <strong>Variável → Personalizadas</strong>.
          </Typography>
          <Paper
            variant="outlined"
            style={{ padding: 12, marginTop: 8, backgroundColor: '#f8fafc' }}
          >
            <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
              <TextField
                variant="outlined"
                size="small"
                label="Nome da variável"
                placeholder="Ex.: Valor da multa"
                value={novaVar}
                error={Boolean(erroVar)}
                helperText={erroVar || ' '}
                onChange={(e) => {
                  setNovaVar(e.target.value);
                  setErroVar('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    adicionarVariavel();
                  }
                }}
                inputProps={{ maxLength: 60 }}
                style={{ flex: 1, minWidth: 220 }}
              />
              <TextField
                select
                variant="outlined"
                size="small"
                label="Tipo"
                value={tipoNovaVar}
                onChange={(e) => setTipoNovaVar(e.target.value)}
                helperText=" "
                style={{ width: 150 }}
              >
                {TIPOS_VARIAVEL.map(({ valor, rotulo, Icone }) => (
                  <MenuItem key={valor} value={valor}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Icone fontSize="small" color="action" />
                      {rotulo}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              {tipoNovaVar === 'data' && (
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  label="Formato da data"
                  value={formatoNovaVar}
                  onChange={(e) => setFormatoNovaVar(e.target.value)}
                  helperText=" "
                  style={{ minWidth: 260 }}
                >
                  {FORMATOS_DATA.map(({ valor, rotulo }) => (
                    <MenuItem key={valor} value={valor}>
                      {rotulo}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Button
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!novaVar.trim()}
                onClick={adicionarVariavel}
                style={{ height: 40 }}
              >
                Adicionar
              </Button>
            </Box>
          </Paper>
          <Box mt={2}>
            <Typography variant="overline" color="textSecondary">
              {`Variáveis criadas (${listaVar.length}/10)`}
            </Typography>
            {listaVar.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: '12px 0' }}
              >
                Nenhuma variável criada ainda.
              </Typography>
            ) : (
              listaVar.map((item, indice) => {
                const tipo =
                  TIPOS_VARIAVEL.find((t) => t.valor === item.tipo) ||
                  TIPOS_VARIAVEL[0];
                return (
                  <Paper
                    key={chaveDoRotulo(item.rotulo)}
                    variant="outlined"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '6px 12px',
                      marginTop: 8,
                    }}
                  >
                    <tipo.Icone color="primary" />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {item.rotulo}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {`{{${chaveDoRotulo(item.rotulo)}}}`}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        item.tipo === 'data' && item.formato === 'extenso'
                          ? `${tipo.rotulo} · por extenso`
                          : tipo.rotulo
                      }
                    />
                    <Tooltip title="Remover variável">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setValue(
                            'variaveis',
                            listaVar.filter((_, i) => i !== indice),
                          );
                          atualizarVar({});
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setDialogoVar(false)}>
            Concluir
          </Button>
        </DialogActions>
      </Dialog>
      <VisualizadorPdf
        url={pdfVisto}
        tipo={pdfTipo}
        titulo={getValue('nome')}
        subtitulo={pdfSubtitulo}
        onClose={fecharPdf}
      />
      <Secao titulo="Assinatura" visible={isAssinar} deps={['acao']}>
        <Linha>
          <SelectCampo
            name="maisAssinaturas"
            label="Mais de uma assinatura"
            opcoes={[
              { valor: 'nao', label: 'Não' },
              { valor: 'sim', label: 'Sim' },
            ]}
            ajuda="Sim: a assinatura 1 é do colaborador e as demais de pessoas fixas, em qualquer ordem"
            deps={['acao']}
            visible={isAssinar}
          />
          <SelectCampo
            name="quem_assina"
            label="Quem assina"
            opcoes={OPCOES_QUEM_ASSINA}
            deps={['acao', 'maisAssinaturas']}
            visible={(values) =>
              isAssinar(values) && !usaMaisAssinaturas(values)
            }
          />
          <AutoComleteRemoto
            name="colaboradorFixo"
            label="Colaborador que assina"
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.nome}`}
            loadOptions={(termo) =>
              getListAPI(
                'colaboradores',
                ['nome', 'asc'],
                [1, 10],
                {
                  nome: termo,
                  status: STATUS_ATIVO,
                  is_user: true,
                },
                [],
              )
            }
            fullWidth
            deps={['acao', 'quem_assina', 'maisAssinaturas']}
            visible={isUsuarioFixo}
          />
        </Linha>
        <AutoComleteRemoto
          name="assinantesAdicionais"
          label="Assinaturas 2, 3... (pessoas fixas)"
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.nome}`}
          loadOptions={(termo) =>
            getListAPI(
              'colaboradores',
              ['nome', 'asc'],
              [1, 10],
              { nome: termo, status: STATUS_ATIVO, is_user: true },
              [],
            )
          }
          fullWidth
          multiple
          deps={['acao', 'maisAssinaturas']}
          visible={usaMaisAssinaturas}
        />
      </Secao>
      <Secao titulo="Geração">
        <Linha>
          <SelectCampo
            name="recorrencia"
            label="Recorrência"
            opcoes={OPCOES_RECORRENCIA}
            ajuda={textoDaRecorrencia}
            deps={['acao', 'origem', 'variaveis']}
          />
          <SelectCampo
            name="abrangencia"
            label="Abrangência"
            opcoes={OPCOES_ABRANGENCIA}
            ajuda={(getValue) =>
              isPorColaborador({
                acao: getValue('acao'),
                origem: getValue('origem'),
                recorrencia: getValue('recorrencia'),
                variaveis: getValue('variaveis'),
              })
                ? 'Quem aparece na lista do detalhe do modelo: todos os ativos, um colaborador ou vários (lista e/ou filtros).'
                : 'Quem recebe: todos os ativos, um colaborador ou vários (lista e/ou filtros, avaliados a cada geração).'
            }
            deps={['acao', 'origem', 'recorrencia', 'variaveis']}
            visible={isRecorrente}
          />
        </Linha>
        {usaLista({
          acao,
          recorrencia,
          abrangencia,
          origem,
          variaveis: variaveisForm,
        }) &&
          abrangencia === 'varios' && (
            <FiltrosTabela
              campos={CAMPOS_FILTRO_COLABORADOR}
              valores={filtrosColab}
              onChange={(valores) => setValue('filtroColaboradores', valores)}
            />
          )}
        <AutoComleteRemoto
          name="colaboradores"
          label="Colaboradores"
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.nome}`}
          loadOptions={(termo) =>
            getListAPI(
              'colaboradores',
              ['nome', 'asc'],
              [1, 10],
              {
                nome: termo,
                status: STATUS_ATIVO,
                ...filtrosDaLista(filtrosColab),
              },
              [],
            )
          }
          fullWidth
          multiple
          deps={['acao', 'origem', 'recorrencia', 'variaveis', 'abrangencia']}
          visible={usaLista}
        />
      </Secao>
    </div>
  );
}

const Dialogo = ({ onClose, open, id, isDialogoConfirmacaoActions }) => {
  const { enqueueSnackbar } = useSnackbar();

  // O PDF vai logo depois de salvar o modelo (precisa do id). Se o envio falhar,
  // o modelo já existe: o aviso diz para reenviar pela edição.
  async function enviarPdf(resposta, dados) {
    if (!isUpload(dados) || !dados.arquivo_pdf) return;
    try {
      const formData = new FormData();
      formData.append('file', dados.arquivo_pdf);
      await api.post(
        `/modelos_documentos/${id || resposta.data.id}/pdf`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    } catch (e) {
      const mensagem =
        e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao enviar o PDF.';
      enqueueSnackbar(
        `Modelo salvo, mas o PDF não foi enviado: ${mensagem} Edite o modelo para enviar de novo.`,
        { variant: 'error' },
      );
    }
  }

  return (
    <DialogoCreateEdit
      open={open}
      id={id}
      initialValues={initialValues}
      validators={validadores}
      resource="modelos_documentos"
      titleCreate="Novo Modelo de Documento"
      titleUpdate="Editar Modelo de Documento"
      nested={['usuarioFixo']}
      dialogProps={{ fullWidth: true, maxWidth: 'md' }}
      onClose={onClose}
      transformGetDados={transformGetDados}
      transformSalvar={transformSalvar}
      isCloseTitle
      isDialogoConfirmacaoActions={isDialogoConfirmacaoActions}
      aposSalvar={enviarPdf}
    >
      <Form id={id} />
    </DialogoCreateEdit>
  );
};

export default Dialogo;
